/* istanbul ignore file */ /* this file is not used or will be deleted in other branches */
// @ts-nocheck
import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import { useTranslation } from 'react-i18next';
import { useDdf } from '../context/ddf-context';
import { useListInteractions, AddButton } from '@td-client-frontend/common';
import { AlternativeAdd } from '@td-client-frontend/icons';

export function Tabs({
  fields,
  label: tabComponentLabel,
  additonalButton,
  tabsNavButtonsWithBorder,
  initialTab = 0,
  trackActiveTab = false
}) {
  const [active, setActive] = useState(0);
  const { change, getState, renderForm } = useFormApi();
  const { readOnly, isInModal, handlers, optionalValues } = useDdf();
  const enabledTabs = useMemo(
    () => (optionalValues?.isOptionTabsHide ? fields.filter((field) => !field.isOptional) : [...fields]),
    [fields, optionalValues]
  );
  const { t } = useTranslation('form'); // TODO move to api level
  const { buttonLabel, hiddenInReadOnly, activeForTab, handler, hiddenIf } = additonalButton || {};

  useEffect(() => {
    if (trackActiveTab && handlers['trackActiveTab']) {
      const activeTabChangeHandler = handlers['trackActiveTab'] || function () {};

      activeTabChangeHandler(active);
    }
  }, [active, handlers, trackActiveTab]);

  useEffect(() => {
    // reset an active tab to the default one if tabs count changed
    if (active > enabledTabs.length - 1) {
      setActive(0);
    }
  }, [enabledTabs.length, active]);

  useEffect(() => {
    const initialTab = getState().values?.formConfig?.initialTab;
    if (initialTab) {
      setActive(initialTab);
      if (handlers['hanleInitialTab']) {
        handlers['hanleInitialTab'](change);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackActiveTab]);

  const { listWrapperRef } = useListInteractions<HTMLDivElement>({
    next: 39,
    prev: 37,
    select: [13, 32],
    itemsNumber: enabledTabs.length,
    onSelect: setActive,
    active
  });

  let additionalRightButtonIsApplicableForForm = false;
  if (additonalButton && buttonLabel) {
    if (readOnly) {
      additionalRightButtonIsApplicableForForm = !hiddenInReadOnly;
    } else {
      if (hiddenIf) {
        const formValues = getState().values;

        additionalRightButtonIsApplicableForForm = Array.isArray(hiddenIf)
          ? !hiddenIf.some((attrAccess) => formValues[attrAccess])
          : !formValues[hiddenIf];
      } else {
        additionalRightButtonIsApplicableForForm = true;
      }
    }
  }

  const additionalRightButtonIsRequired = additionalRightButtonIsApplicableForForm && active === activeForTab - 1;

  const additionalButtonHandlerWrapper =
    additonalButton && handlers[handler]
      ? () => {
          handlers[handler](change);
        }
      : () => {};

  return (
    <div className={clsx(isInModal && 'mx-6')}>
      <div className="flex flex-row justify-between">
        <div className="flex pt-1 mb-4" role="tablist" aria-label={tabComponentLabel} ref={listWrapperRef}>
          {enabledTabs.map(({ name = '', title = '', fields: { component = '', name: fieldName = '' } }, index) => (
            <button
              type="button"
              key={name}
              className={clsx(
                'cursor-pointer focus:outline-none focus-visible:ring',
                !tabsNavButtonsWithBorder && 'p-4'
              )}
              onClick={() => {
                // get override for footer buttons if there are on the tab
                const override = getState()?.values?.__footerOverride;
                setActive(index);
                // currently override if the first (and only) field in the tab config is a grid
                // (check componentMapper)
                change('__footerOverride', {
                  ...override,
                  // to remove buttons override when clicked on another tab
                  // and bring back when clicked on the tab with override
                  isShown: component === 'grid' && fieldName === override?.name
                });
              }}
              role="tab"
              aria-selected={active === index}
              aria-controls={`tab-pane-${name}`}
              id={name}
              // First item should be focused in usual flow
              // The others - via JS (tabindex -1).
              // When going back from tab pane to tab, the active one should get focus within usual flow (tabindex 0)
              // and the others - via JS.
              tabIndex={active > 0 ? (index === active ? 0 : -1) : index === 0 ? 0 : -1}
            >
              {tabsNavButtonsWithBorder ? (
                <span
                  className={clsx(
                    'my-4 p-3 font-effra border-t-1 border-b-1 border-gray-30 inline-block min-w-12 black-default',
                    active === index && 'border-primary-500 bg-yellow-20',
                    index === 0 && 'border-l-1 rounded-tl-lg rounded-bl-lg border-r-1',
                    index === enabledTabs.length - 1 && 'border-r-1 rounded-tr-lg rounded-br-lg border-l-1'
                  )}
                >
                  {t(title)}
                </span>
              ) : (
                <span
                  className={clsx(
                    'py-4 px-2 font-normal hover:border-b-3 hover:border-primary-500',
                    active === index && 'font-semibold border-primary-500 border-b-3 text-black-default'
                  )}
                >
                  {t(title)}
                </span>
              )}
            </button>
          ))}
        </div>
        <div>
          {additionalRightButtonIsRequired && (
            <AddButton
              Icon={<AlternativeAdd fill="#5b2f91" />}
              label={buttonLabel || 'Add'}
              onClick={additionalButtonHandlerWrapper}
            />
          )}
        </div>
      </div>
      {enabledTabs.map(({ name, fields, helperText }, index) => (
        <div
          style={{
            width: 'calc(100% - 3rem)'
          }}
          key={name}
          className={clsx(
            'focus:outline-none focus-visible:ring',
            !tabsNavButtonsWithBorder && 'mx-6',
            active !== index && 'h-0 overflow-hidden hidden',
            isInModal && 'absolute'
          )}
          role="tabpanel"
          tabIndex={active === index ? 0 : -1}
          id={`tab-pane-${name}`}
          aria-labelledby={name}
          aria-hidden={active !== index}
        >
          {helperText && !readOnly && <p className="py-4 text-sm font-effra text-gray-default">{helperText}</p>}
          {renderForm(fields)}
        </div>
      ))}
    </div>
  );
}
